import { useEffect, useState } from "react";

/**
 * Client-side content protection for course learning & exam pages.
 * NOTE: Client-side protection can be bypassed by determined attackers.
 * This adds deterrents: blocks common devtools shortcuts, right-click,
 * text selection, and shows a warning overlay when devtools appear to be open.
 */
export function useContentProtection(enabled = true) {
  const [devtoolsOpen, setDevtoolsOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Add protective CSS class to body
    document.body.classList.add("protected-content");

    // Block key combos: F12, Ctrl+Shift+I/J/C, Ctrl+U, Ctrl+S, Ctrl+Shift+K
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (
        k === "f12" ||
        (e.ctrlKey && e.shiftKey && ["i", "j", "c", "k"].includes(k)) ||
        (e.metaKey && e.altKey && ["i", "j", "c"].includes(k)) ||
        (e.ctrlKey && ["u", "s"].includes(k)) ||
        (e.metaKey && ["s"].includes(k))
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const onContext = (e) => {
      e.preventDefault();
      return false;
    };

    const onDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    const onCopy = (e) => {
      // Allow copy inside inputs/textareas only
      const t = e.target;
      const editable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (!editable) {
        e.preventDefault();
        return false;
      }
    };

    // Devtools detection via window size heuristic
    const threshold = 170;
    const check = () => {
      const wDiff = window.outerWidth - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      const open = wDiff > threshold || hDiff > threshold;
      setDevtoolsOpen(open);
    };

    document.addEventListener("keydown", onKey, true);
    document.addEventListener("contextmenu", onContext, true);
    document.addEventListener("dragstart", onDragStart, true);
    document.addEventListener("copy", onCopy, true);
    document.addEventListener("cut", onCopy, true);
    window.addEventListener("resize", check);
    const interval = setInterval(check, 1200);
    check();

    return () => {
      document.body.classList.remove("protected-content");
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("contextmenu", onContext, true);
      document.removeEventListener("dragstart", onDragStart, true);
      document.removeEventListener("copy", onCopy, true);
      document.removeEventListener("cut", onCopy, true);
      window.removeEventListener("resize", check);
      clearInterval(interval);
    };
  }, [enabled]);

  return { devtoolsOpen };
}
